import React, { useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { capitalize } from 'lodash';
import { TeamMember } from 'Interfaces/Team';
import { Avatar } from 'Components/Avatar/Avatar';
import KeyIcon from 'Assets/images/icons/key-icon.svg';
import RemoveIcon from 'Assets/images/icons/remove-icon.svg';
import PauseIcon from 'Assets/images/icons/pause.svg';
import PlayIcon from 'Assets/images/icons/play.svg';
import { UserRoles } from 'Interfaces/User';
import DropDownOptionsMobile from 'Pages/Documents/components/DocumentItem/DropDownOptionsMobile';
import {
  useTeamMembersDelete,
  useTeamMembersFreeze,
  useTeamMembersUnfreeze,
  useToAdminUpgrade,
  useToUserDowngrade,
} from 'Hooks/Team';
import Toast from 'Services/Toast';
import UISpinner from 'Components/UIComponents/UISpinner';
import { useModal } from 'Hooks/Common';
import DeleteModal from 'Components/DeleteModal';
import UICheckbox from 'Components/UIComponents/UICheckbox';
import FreezeTeammateModal from './FreezeTeammateModal';
import { formatDate } from 'Utils/formatters';
import ConfirmModal from 'Components/ConfirmModal';
import { LtdTypes } from 'Interfaces/Billing';

interface TeamMemberItemMobileProps {
  teamMember: TeamMember;
  isChangingRoleEnabled: boolean;
  isEditEnabled: boolean;
  toggleSelect?: () => void;
  isSelectable?: boolean;
  isSelected?: boolean;
  className?: string;
  ltdType: LtdTypes;
}

const TeamMemberItemMobile = ({
  teamMember,
  isChangingRoleEnabled,
  isEditEnabled,
  isSelected,
  toggleSelect = () => {},
  isSelectable,
  className,
  ltdType,
}: TeamMemberItemMobileProps) => {
  const [upgradeToAdmin, isUpgradingToAdmin] = useToAdminUpgrade();
  const [downgradeToUser, isDowngradeToUser] = useToUserDowngrade();
  const [deleteTeamMembers, isTeamMemberDeleting] = useTeamMembersDelete();
  const [freezeTeamMembers, isTeamMemberFreezing] = useTeamMembersFreeze();
  const [unfreezeTeamMembers, isTeamMemberUnfreezing] = useTeamMembersUnfreeze();

  const handleDeleteTeamMember = useCallback(async () => {
    try {
      await deleteTeamMembers([teamMember.id]);

      Toast.success('Team member deleted successfully');
    } catch (error) {
      Toast.handleErrors(error);
    }
  }, [deleteTeamMembers, teamMember]);

  const handleFreezeTeamMember = useCallback(
    async (freezeUntil: string) => {
      try {
        await freezeTeamMembers({ teamMembersIds: [teamMember.id], freezeUntil });

        Toast.success('Team member frozen successfully');
      } catch (error) {
        Toast.handleErrors(error);
      }
    },
    [freezeTeamMembers, teamMember],
  );

  const handleUnfreezeTeamMembers = useCallback(async () => {
    try {
      await unfreezeTeamMembers({ teamMembersIds: [teamMember.id] });

      Toast.success('Team members unfrozen successfully');
    } catch (error) {
      Toast.handleErrors(error);
    }
  }, [unfreezeTeamMembers, teamMember]);

  const [showDeleteModal, hideDeleteModal] = useModal(
    () => (
      <DeleteModal
        onClose={hideDeleteModal}
        onConfirm={() => {
          handleDeleteTeamMember();
          hideDeleteModal();
        }}
        className="documents__deleteWrapper"
        deleteTitle="Delete User"
      >
        <div className="documents__deleteHeader">
          <h5 className="documents__deleteTitle">
            Are you sure you want to delete this team member?
          </h5>
          <p className="modal__subTitle">
            If you want to remove the user so they can&apos;t access Signaturely anymore,
            please continue.
          </p>
        </div>
      </DeleteModal>
    ),
    [handleDeleteTeamMember],
  );

  const [showFreezeModal, hideFreezeModal] = useModal(
    () => (
      <FreezeTeammateModal
        onClose={hideFreezeModal}
        onFreezeTeammate={handleFreezeTeamMember}
      />
    ),
    [handleFreezeTeamMember],
  );

  const [showUnfreezeModal, hideUnfreezeModal] = useModal(
    () => (
      <ConfirmModal
        onConfirm={() => {
          handleUnfreezeTeamMembers();
          hideUnfreezeModal();
        }}
        onClose={hideUnfreezeModal}
        onCancel={hideUnfreezeModal}
        confirmText={'Unfreeze'}
        isCancellable={true}
      >
        <div className="modal__header">
          <h4 className="modal__title">Do you want to unfreeze this team member?</h4>
        </div>
        <p className="modal__subTitle modal__subTitle--center">
          This action will result in the charge for additional team members for the
          remaining payment period.
        </p>
      </ConfirmModal>
    ),
    [handleUnfreezeTeamMembers],
  );

  const handleToAdminUpgrade = useCallback(async () => {
    try {
      await upgradeToAdmin({ userId: teamMember.id });

      Toast.success('Team member successfully upgraded to admin.');
    } catch (err) {
      Toast.handleErrors(err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleToUserDowngrade = useCallback(async () => {
    try {
      await downgradeToUser({ userId: teamMember.id });

      Toast.success('Team member successfully downgraded to user.');
    } catch (err) {
      Toast.handleErrors(err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isOptionProcessing =
    isUpgradingToAdmin ||
    isDowngradeToUser ||
    isTeamMemberFreezing ||
    isTeamMemberUnfreezing ||
    isTeamMemberDeleting;

  const options = useMemo(
    () => [
      {
        name: 'Upgrade to Admin',
        icon: KeyIcon,
        hidden:
          teamMember.role !== UserRoles.USER ||
          !isChangingRoleEnabled ||
          !!teamMember.frozenUntil,
        disabled: isOptionProcessing,
        onClick: handleToAdminUpgrade,
      },
      {
        name: 'Downgrade to User',
        icon: KeyIcon,
        hidden:
          teamMember.role !== UserRoles.ADMIN ||
          !isChangingRoleEnabled ||
          !!teamMember.frozenUntil,
        disabled: isOptionProcessing,
        onClick: handleToUserDowngrade,
      },
      {
        name: 'Freeze',
        icon: PauseIcon,
        classNameIcon: 'documents__dropdownOption--blue-icon',
        hidden:
          teamMember.role === UserRoles.OWNER ||
          !isChangingRoleEnabled ||
          ltdType !== LtdTypes.NONE ||
          !!teamMember.frozenUntil,
        disabled: isOptionProcessing,
        onClick: showFreezeModal,
      },
      {
        name: 'Unfreeze',
        icon: PlayIcon,
        iconClassName: 'documents__dropdownOption--blue-icon',
        hidden:
          teamMember.role === UserRoles.OWNER ||
          !isChangingRoleEnabled ||
          ltdType !== LtdTypes.NONE ||
          !teamMember.frozenUntil,
        disabled: isOptionProcessing,
        onClick: showUnfreezeModal,
      },
      {
        name: 'Remove from team',
        icon: RemoveIcon,
        classNameIcon: 'documents__dropdownOption--red-icon',
        hidden: teamMember.role === UserRoles.OWNER || !isChangingRoleEnabled,
        disabled: isOptionProcessing,
        onClick: showDeleteModal,
        className: 'red',
      },
    ],
    [
      teamMember.role,
      teamMember.frozenUntil,
      isChangingRoleEnabled,
      isOptionProcessing,
      handleToAdminUpgrade,
      handleToUserDowngrade,
      ltdType,
      showFreezeModal,
      showUnfreezeModal,
      showDeleteModal,
    ],
  );

  const hasActions = useMemo(() => !!options.filter(option => !option.hidden).length, [
    options,
  ]);

  return (
    <div
      className={classNames(
        'table__row',
        'table__dataRow--teammateItem mobile',
        className,
      )}
    >
      <div className="table__column table__column--check">
        {isSelectable && (
          <UICheckbox handleClick={() => toggleSelect()} check={isSelected} />
        )}
      </div>
      <div className="team__member mobile">
        <Avatar
          name={teamMember.name}
          email={teamMember.email}
          avatarUrl={teamMember.avatarUrl}
        />
      </div>
      <div className="table__column table__column--text mobileTeamPage">
        <div className="team__member-name mobile">{teamMember.name}</div>
        <div className="table__column table__column--status">
          {teamMember.frozenUntil
            ? `Frozen until ${formatDate(teamMember.frozenUntil)}`
            : 'Active'}
        </div>
        <div className="table__column table__column--status">
          {capitalize(teamMember.role)}
        </div>
        <div className="table__column table__column--team-email mobile">
          {teamMember.email}
        </div>
      </div>
      {(isEditEnabled || isChangingRoleEnabled) && (
        <div
          className={classNames(
            'team__column-actions',
            'table__column',
            'table__column--action',
            {
              'table__column--hidden': !hasActions,
            },
          )}
        >
          {isOptionProcessing ? (
            <UISpinner wrapperClassName="spinner--main__wrapper" width={20} height={20} />
          ) : (
            <DropDownOptionsMobile options={options} anchorClassName="table__container" />
          )}
        </div>
      )}
    </div>
  );
};

export default TeamMemberItemMobile;
